import {
    collection,
    query,
    getDocs,
    getDoc,
    addDoc,
    doc,
    onSnapshot,
    where,
} from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { BreadOrder, Bread, BreadOrderStatus } from '../models/BreadOrder';
import { db } from '../firebase/firebase';
import {
    writeCreateAudit,
    softDeleteWithAudit,
    writeWithAudit,
    computeOrderDiff,
} from './orderAudit.service';
import customerService from './customer.service';
import userService from './user.service';
import { normalizePhoneForOrder } from '../helpers/phone';


class BreadOrderService {
    private collection: string;
    private unsubscribe: (() => void) | null; // Declare unsubscribe

    constructor() {
        this.collection = 'breads';
        this.unsubscribe = null; // Initialize as null
    }

    async createBreadOrder(
        date: string,
        time: string,
        firstName: string,
        lastName: string,
        phoneNumber: string,
        breads: Array<Bread>,
        comments: string,
        cashier: string,
        createdAt: Date,
        customerId: string | null = null
    ): Promise<BreadOrder> {
        // Normalize phone number to E.164 format
        if (phoneNumber) {
            try {
                phoneNumber = normalizePhoneForOrder(phoneNumber);
            } catch (error) {
                throw new Error((error as Error).message || 'Invalid phone number format. Please correct the phone number.');
            }
        }

        // Find or create customer if customerId not provided
        let finalCustomerId = customerId;
        if (!finalCustomerId && phoneNumber) {
            try {
                const customer = await customerService.findOrCreate(phoneNumber, {
                    firstName,
                    lastName
                });
                finalCustomerId = customer.id;
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error finding/creating customer:', error);
            }
        }

        const userStoreId = await userService.getUserStoreId()
        const breadCollectionRef = collection(db, this.collection);
        const breadOrder = new BreadOrder(
            {id: '',
            date,
            time,
            firstName,
            lastName,
            phoneNumber,
            bread: breads,
            comments,
            cashier,
            createdAt,
            status: 'pending',
            store_id: userStoreId,
            customerId: finalCustomerId
            }
        );
        try {
            const docRef = await addDoc(breadCollectionRef, breadOrder.toJson());
            breadOrder.id = docRef.id;

            // Audit: record creation
            try {
                await writeCreateAudit(this.collection, docRef.id);
            } catch (auditError) {
                console.error('Error writing create audit:', auditError);
            }

            // Record order on customer
            if (finalCustomerId) {
                try {
                    await customerService.recordOrder(finalCustomerId);
                } catch (error) {
                    Sentry.captureException(error);
                    console.error('Error recording order on customer:', error);
                }
            }

            return breadOrder;
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error creating bread order:', error);
            throw error;
        }
    }

    async fetchBreads(): Promise<BreadOrder[]> {
        const userStoreId = await userService.getUserStoreId();
        const breadCollectionRef = collection(db, this.collection);
        const q = query(breadCollectionRef, where("store_id", "==", userStoreId));
        const querySnapshot = await getDocs(q);
        const breads: BreadOrder[] = [];
        querySnapshot.forEach((doc) => {
            if (!doc.data().deleted) {
                breads.push(BreadOrder.fromFirebase(doc));
            }
        });
        return breads;
    }


    async fetchBreadsWithListeners(all: boolean, callback: (breads: BreadOrder[]) => void): Promise<void> {
        const userStoreId = await userService.getUserStoreId();
        const breadCollectionRef = collection(db, this.collection);
        let q;

        if (!all) {
            let cutoffDate = new Date()
            cutoffDate.setDate(cutoffDate.getDate() - 1);
            const cutoffDateString = cutoffDate.toISOString().split('T')[0]; // format to YYYY-MM-DD

            console.log(`Fetching breads for storeId: ${userStoreId} with cutoff date: ${cutoffDateString}`);

            q = query(
                breadCollectionRef,
                where("store_id", "==", userStoreId),   
                where("date", ">=", cutoffDateString),       
            );
        } else {
            q = query(
                breadCollectionRef,
                where("store_id", "==", userStoreId),        
            );
        }
    
        this.unsubscribe = onSnapshot(q, (snapshot) => {
            const breads: BreadOrder[] = snapshot.docs
                .filter((doc) => !doc.data().deleted)
                .map((doc) => BreadOrder.fromFirebase(doc));
            console.log("Real-time breads:", breads);
            breads.sort((a, b) => {
                if (a.date === b.date) {
                    return a.time.localeCompare(b.time);
                }
                return b.date.localeCompare(a.date);
            });
            callback(breads); 
        });
    }

   

    async editBreadOrder(orderId: string, updatedData: Partial<BreadOrder>): Promise<void> {
        const docRef = doc(db, this.collection, orderId);

        // Read current state for diff
        const currentSnap = await getDoc(docRef);
        if (!currentSnap.exists()) {
            throw new Error('Orden no encontrada');
        }
        const currentData = currentSnap.data();

        const updatedDataJson = new BreadOrder(
            {
            id: orderId,
            date: updatedData.date!,
            time: updatedData.time!,
            firstName: updatedData.firstName!,
            lastName: updatedData.lastName!,
            phoneNumber: updatedData.phoneNumber!,
            bread: updatedData.bread!,
            comments: updatedData.comments!,
            cashier: updatedData.cashier!,
            createdAt: updatedData.createdAt!,
            status: updatedData.status!,
            store_id: updatedData.store_id!,
            }
        ).toJson();

        const changes = computeOrderDiff(currentData, updatedDataJson);
        if (Object.keys(changes).length === 0) return;

        try {
            await writeWithAudit(this.collection, orderId, updatedDataJson, 'updated', changes);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error updating bread order:', error);
            throw error;
        }
    }

    async deleteBreadOrder(breadId: string): Promise<void> {
        try {
            await softDeleteWithAudit(this.collection, breadId);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting bread order:', error);
            throw error;
        }
    }

    async updateOrderStatus(
        orderId: string,
        status: BreadOrderStatus
    ): Promise<void> {
        const docRef = doc(db, this.collection, orderId);
        try {
            const currentSnap = await getDoc(docRef);
            const currentData = currentSnap.exists() ? currentSnap.data() : {};
            const changes = computeOrderDiff(currentData, { status });
            if (Object.keys(changes).length === 0) return;

            await writeWithAudit(this.collection, orderId, { status }, 'updated', changes);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error updating bread order status:', error);
            throw error;
        }
    }
}


const breadService = new BreadOrderService();
export default breadService;
